"use client";

import { Area, Category } from "@prisma/client";
import { Gift, Mail, MapPin, Phone, User } from "lucide-react";

import { PageFormTemplate } from "../components/PageFormTemplate";

import {
  FormInput,
  FormRadioGroup,
  FormSelect,
  FormTextarea,
} from "@/components/forms/components/FormInputs";
import { createOfferAction } from "@/lib/actions/offers";
import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";
import type { OrganizationTerminology } from "@/lib/services/terminologyService";
import { OfferFormData, offerSchema } from "@/lib/validations/offers";

interface OfferDonationFormProps {
  areas: Area[];
  categories: Category[];
  organizationId: string;
  successRedirectUrl?: string;
  terminology: OrganizationTerminology;
}

/**
 * Public offer donation form component
 */
export function OfferDonationForm({
  areas,
  categories,
  organizationId,
  successRedirectUrl = "/offer-donation/thank-you",
  terminology,
}: OfferDonationFormProps) {
  // Default values for the form
  const defaultValues: OfferFormData = {
    areaId: "",
    categoryId: "",
    condition: undefined,
    description: "",
    email: "",
    firstName: "",
    imageUrl: "",
    lastName: "",
    offerType: "PHYSICAL_ITEM",
    phone: "",
    quantity: undefined,
    title: "",
  };

  // Form submission handler
  const handleFormSubmit = async (data: OfferFormData) => {
    console.log("🚀 handleFormSubmit called with data:", data);
    console.log("🚀 Organization ID:", organizationId);

    // Add organizationId to the data
    const dataWithOrg = {
      ...data,
      organizationId,
    };

    console.log("🚀 Calling createOfferAction with:", dataWithOrg);

    try {
      console.log("🚀 About to call createOfferAction...");
      const result = await createOfferAction(dataWithOrg);

      console.log("🚀 createOfferAction result:", result);

      return result;
    } catch (error) {
      console.error("🚨 Error in handleFormSubmit:", error);
      throw error;
    }
  };

  // Error handler for validation errors
  const handleError = (error: Error) => {
    void EventPublisher.publishError(
      EventKeys.PUBLIC_OFFER_DONATION_FORM_SUBMIT_FAILURE,
      error,
      { organizationId },
    );
  };

  return (
    <PageFormTemplate
      callbackUrl={successRedirectUrl}
      cancelLabel="Cancel"
      defaultValues={defaultValues}
      description="Share what you'd like to donate to help others in your community"
      eventKeys={{
        attempt: EventKeys.PUBLIC_OFFER_DONATION_FORM_SUBMIT_ATTEMPT,
        failure: EventKeys.PUBLIC_OFFER_DONATION_FORM_SUBMIT_FAILURE,
        success: EventKeys.PUBLIC_OFFER_DONATION_FORM_SUBMIT_SUCCESS,
      }}
      formSchema={offerSchema}
      icon="Gift"
      submitLabel="Submit Donation Offer"
      terminology={terminology}
      title="Donation Offer"
      onError={handleError}
      onSubmit={handleFormSubmit}
    >
      {({ control, watch }) => {
        const offerType = watch("offerType");

        return (
          <div className="space-y-6">
            {/* What are you offering? */}
            <div>
              <div className="my-4">
                <h3 className="text-md font-medium">What are you offering?</h3>
                <p className="text-sm text-default-600">
                  Tell us about the item, service, or financial assistance you'd
                  like to donate
                </p>
              </div>

              <div className="space-y-4">
                <FormInput
                  required
                  aria-required="true"
                  control={control}
                  label="Title"
                  name="title"
                  placeholder="e.g., Queen bed frame in good condition"
                  startContent={
                    <Gift
                      aria-hidden="true"
                      className="text-default-400 h-4 w-4"
                    />
                  }
                />

                <FormTextarea
                  required
                  aria-required="true"
                  control={control}
                  label="Description"
                  name="description"
                  placeholder="Provide details about what you're offering, including condition, size, or any other relevant information..."
                />

                <FormRadioGroup
                  control={control}
                  name="offerType"
                  options={[
                    {
                      label: "Physical Item (furniture, clothing, food, etc.)",
                      value: "PHYSICAL_ITEM",
                    },
                    {
                      label: "Financial Assistance (monetary donation)",
                      value: "FINANCIAL_ASSISTANCE",
                    },
                    {
                      label: "Service/Time (volunteer work, skills, etc.)",
                      value: "PEOPLE_NEEDED",
                    },
                  ]}
                />

                {offerType === "PHYSICAL_ITEM" && (
                  <>
                    <FormInput
                      control={control}
                      label="Quantity"
                      name="quantity"
                      placeholder="e.g., 2"
                      type="number"
                    />

                    <FormSelect
                      control={control}
                      label="Condition"
                      name="condition"
                      options={[
                        { label: "New", value: "new" },
                        { label: "Like New", value: "like-new" },
                        { label: "Good", value: "good" },
                        { label: "Fair", value: "fair" },
                      ]}
                      placeholder="Select condition"
                    />
                  </>
                )}

                {(offerType === "FINANCIAL_ASSISTANCE" ||
                  offerType === "PEOPLE_NEEDED") && (
                  <FormInput
                    control={control}
                    label={
                      offerType === "FINANCIAL_ASSISTANCE"
                        ? "Amount (in dollars)"
                        : "Number of people"
                    }
                    name="quantity"
                    placeholder={
                      offerType === "FINANCIAL_ASSISTANCE"
                        ? "e.g., 100"
                        : "e.g., 2"
                    }
                    type="number"
                  />
                )}

                <FormSelect
                  control={control}
                  label="Category (optional)"
                  name="categoryId"
                  options={categories.map((category) => ({
                    label: category.name,
                    value: category.id,
                  }))}
                  placeholder="Select a category"
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <div className="my-4">
                <h3 className="text-md font-medium">Location</h3>
                <p className="text-sm text-default-600">
                  Where are you located or where can you deliver?
                </p>
              </div>

              <FormSelect
                required
                aria-required="true"
                control={control}
                label={terminology.area.singular}
                name="areaId"
                options={areas.map((area) => ({
                  label: `${area.name}, ${area.state}`,
                  value: area.id,
                }))}
                placeholder={`Select your ${terminology.area.singular.toLowerCase()}`}
                startContent={
                  <MapPin
                    aria-hidden="true"
                    className="text-default-400 h-4 w-4"
                  />
                }
              />
            </div>

            {/* Contact Information */}
            <div>
              <div className="my-4">
                <h3 className="text-md font-medium">Contact Information</h3>
                <p className="text-sm text-default-600">
                  We'll use this to connect you with someone who needs your
                  donation
                </p>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormInput
                    control={control}
                    label="First Name"
                    name="firstName"
                    placeholder="Enter first name"
                    startContent={
                      <User
                        aria-hidden="true"
                        className="text-default-400 h-4 w-4"
                      />
                    }
                  />

                  <FormInput
                    control={control}
                    label="Last Name"
                    name="lastName"
                    placeholder="Enter last name"
                    startContent={
                      <User
                        aria-hidden="true"
                        className="text-default-400 h-4 w-4"
                      />
                    }
                  />
                </div>

                <FormInput
                  required
                  aria-required="true"
                  control={control}
                  label="Email Address"
                  name="email"
                  placeholder="Enter email address"
                  startContent={
                    <Mail
                      aria-hidden="true"
                      className="text-default-400 h-4 w-4"
                    />
                  }
                  type="email"
                />

                <FormInput
                  control={control}
                  label="Phone Number"
                  name="phone"
                  placeholder="Enter phone number (optional)"
                  startContent={
                    <Phone
                      aria-hidden="true"
                      className="text-default-400 h-4 w-4"
                    />
                  }
                />
              </div>
            </div>
          </div>
        );
      }}
    </PageFormTemplate>
  );
}
